import { CSSProperties, useContext, useState, useEffect } from 'react'
import { clsx, styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import useBrand from 'Hooks/useBrand'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import millify from 'millify'
import { CURRENCY_CODE_DATA } from 'helpers/currencies'
import { PlanType, AdConnectionType } from 'modules/Const'
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext'
import CardSymphony from 'elements/CardSymphony'
import Icon from 'elements/Icon'
import ConnectionStep, { ConnectionStepProps } from 'pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep'
import AdConnectionModal from 'components/shareable/AdConnectionModal'
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA'

interface ConnectionsTabProps {
    className?: string
    style?: CSSProperties
}

const ConnectionsTab = ({
    className,
    style
}: ConnectionsTabProps) => {
    const { isProBrand, currentBrand } = useBrand()
    const { handleOpenModal } = useContext(UpgradeModalContext)

    const {
        loggedInFacebookUser,
        isLoggingIntoFacebook,
        loginWithFacebook,
        selectedFacebookAdAccount,
        selectedFacebookPixel,
        selectedFacebookPage,
        selectedInstagramPage,
        initializeFB,
    } = useFacebookConnections()

    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false)
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null)

    // Initialize Facebook connections when component mounts
    useEffect(() => {
        if (currentBrand) {
            initializeFB(currentBrand)
        }
    }, [currentBrand])

    /**
     * Handles pixel connection click - checks pro status first
     * Shows upgrade UI for non-pro users, opens connection modal for pro users
     */
    const handlePixelConnectionClick = () => {
        if (!isProBrand) {
            handleOpenModal({
                source: 'Settings - Facebook Pixel Connection',
                highlightedPlan: PlanType.PRO,
                upgradeCallToAction: 'Unlock Facebook Pixel tracking and advanced conversions with Symphony Pro'
            })
            return
        }
        setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PIXEL)
    }

    const renderConnectionsBanner = () => {
        return (
            <CardSymphony
                className="connectionsTabBanner"
                title="Default connections for all future campaigns"
                subtitle="These connections will be used as defaults for all new campaigns. If you manage multiple ad accounts, we recommend creating separate Symphony profiles for each account."
                size="small"
                style={{ backgroundColor: Colors.purpleLighter }}
            />
        )
    }

    const renderConnectionSteps = () => {
        let selectedAdAccountCurrency = selectedFacebookAdAccount?.currency || 'USD'
        let selectedAdAccountCurrencyName = CURRENCY_CODE_DATA[selectedAdAccountCurrency]?.name_plural || 'United States Dollar'

        const connectionSteps: ConnectionStepProps[] = [
            {
                stepTitle: 'Facebook Profile',
                connectedAccountName: loggedInFacebookUser?.name || 'Facebook Profile',
                connectedAccountIcon: <Icon
                    className="connectionStepIcon"
                    icon={loggedInFacebookUser?.picture?.data.url || 'facebook'}
                    iconType={loggedInFacebookUser ? "custom" : "default"}
                    size={40}
                />,
                stepDescription: 'Login with your Facebook profile to connect your advertising accounts and pages.',
                isCompleted: Boolean(loggedInFacebookUser),
                isLoading: isLoggingIntoFacebook,
                onButtonClick: loginWithFacebook
            },
            {
                stepTitle: 'Ad Account',
                connectedAccountName: selectedFacebookAdAccount?.name || 'Connect your Ad Account',
                connectedAccountIcon: <Icon
                    className="connectionStepIcon"
                    icon="facebook"
                    size={40}
                />,
                stepDescription: 'Your Facebook ad account where Symphony will publish your ads. You\'ll be billed based on your ad account\'s billing method.',
                isCompleted: Boolean(selectedFacebookAdAccount),
                onButtonClick: () => {
                    setSelectedAdConnectionType(AdConnectionType.FACEBOOK_AD_ACCOUNT)
                },
                connectedAccountDescriptions: selectedFacebookAdAccount ? [
                    `$${millify(selectedFacebookAdAccount?.amount_spent || 0)} spent`,
                    `Uses ${selectedAdAccountCurrencyName} (${selectedAdAccountCurrency})`
                ] : undefined,
                canEdit: true,
            },
            {
                stepTitle: 'Facebook Page',
                connectedAccountName: selectedFacebookPage?.name || 'Connect your Facebook Page',
                connectedAccountIcon: <Icon
                    className="connectionStepIcon"
                    icon={selectedFacebookPage?.picture?.data.url || 'facebook'}
                    iconType={selectedFacebookPage ? 'custom' : 'default'}
                    size={40}
                />,
                stepDescription: 'The Facebook Page that will be used for your ads and page promotion campaigns.',
                isCompleted: Boolean(selectedFacebookPage),
                onButtonClick: () => {
                    setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
                },
                canEdit: true,
            }
        ]

        // Add Instagram page step if Facebook page is connected
        if (selectedFacebookPage) {
            connectionSteps.push({
                stepTitle: 'Instagram Page',
                connectedAccountName: selectedInstagramPage?.instagramAccount?.username || 'Connect your Instagram Page',
                connectedAccountIcon: <Icon
                    className="connectionStepIcon"
                    icon={selectedInstagramPage?.instagramAccount?.profile_picture_url || 'instagramFlat'}
                    iconType={selectedInstagramPage ? 'custom' : 'default'}
                    size={40}
                />,
                stepDescription: 'The Instagram page that will be shown in your Instagram ads. Must be connected to your Facebook page.',
                isCompleted: Boolean(selectedInstagramPage),
                onButtonClick: () => {
                    setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
                },
                canEdit: true,
            })
        }

        // Add pixel connection step
        connectionSteps.push({
            stepTitle: 'Facebook Pixel (Ad Campaigns)',
            connectedAccountName: selectedFacebookPixel?.name ?? selectedFacebookPixel?.id ?? 'Connect your Facebook Pixel',
            connectedAccountIcon: <Icon
                className="connectionStepIcon"
                icon={'facebook'}
                iconType="default"
                size={40}
            />,
            stepDescription: isProBrand
                ? 'Connect your Facebook Pixel to enable advanced conversion tracking for better ad campaign performance.'
                : 'Upgrade to Symphony Pro to unlock Facebook Pixel tracking and advanced conversions for ad campaigns.',
            isCompleted: Boolean(selectedFacebookPixel && selectedFacebookPixel?.id),
            onButtonClick: handlePixelConnectionClick,
            canEdit: true,
            iconContainerColor: isProBrand ? Colors.grey : Colors.purpleLighter,
            isLastStep: true
        })

        return (
            <div className="connectionStepsContainer">
                {connectionSteps.map((step, index) => (
                    <ConnectionStep
                        key={`connection-step-${index}`}
                        stepTitle={step.stepTitle}
                        connectedAccountName={step.connectedAccountName}
                        connectedAccountIcon={step.connectedAccountIcon}
                        stepDescription={step.stepDescription}
                        isCompleted={step.isCompleted}
                        onButtonClick={step.onButtonClick}
                        connectedAccountDescriptions={step.connectedAccountDescriptions}
                        isLoading={step.isLoading}
                        canEdit={step.canEdit}
                        iconContainerColor={step.iconContainerColor}
                        isLastStep={step.isLastStep}
                    />
                ))}
            </div>
        )
    }

    const handleCloseModal = () => {
        setSelectedAdConnectionType(null)
        setShowAdConnectionsModal(false)
    }

    // NOTE: render()
    return (
        <div
            className={clsx('connectionsTabComponent', className)}
            style={styx(sty.connectionsTabComponent, style)}
        >
            {renderConnectionsBanner()}
            {renderConnectionSteps()}

            <AdConnectionModal
                isOpen={Boolean(selectedAdConnectionType)}
                onClose={handleCloseModal}
                connectionType={selectedAdConnectionType || undefined}
            />
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    connectionsTabComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
    }
}

export default ConnectionsTab 